import { FC, useEffect, useRef, useState } from 'react';
import { 
  X, 
  User,
  CheckCircle,
  Timer,
  MessageSquare,
  Phone,
  Users,
  AlertTriangle,
  HandshakeIcon
} from 'lucide-react';
import { Customer } from '../../lib/types';
import { CustomerInteractionRecord } from '../../store/slices/interactionsSlice';
import { formatDistanceToNow } from 'date-fns';
import { enUS } from 'date-fns/locale';
import { directusOperations, resolveUserName } from '../../lib/directus';
import { Badge } from './badge';
import { useLanguage } from '../../contexts/LanguageContext';

interface CustomerActivityModalProps {
  customer: Customer;
  isOpen: boolean;
  onClose: () => void;
  currentUserId?: string;
}

interface ActivityInteraction extends CustomerInteractionRecord {
  status: 'pending' | 'completed' | 'overdue';
  isCurrentUser: boolean;
}

export const CustomerActivityModal: FC<CustomerActivityModalProps> = ({
  customer,
  isOpen,
  onClose,
  currentUserId
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [interactions, setInteractions] = useState<ActivityInteraction[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useLanguage();


  // Auto-scroll to top when modal opens
  useEffect(() => {
    if (isOpen && scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  }, [isOpen]);

  // Load customer interactions when modal opens
  useEffect(() => {
    console.log('🔄 CustomerActivityModal useEffect triggered:', { isOpen, customerId: customer.id });
    if (isOpen && customer.id) {
      console.log('✅ Conditions met, calling loadCustomerInteractions...');
      loadCustomerInteractions();
    } else {
      console.log('❌ Conditions not met:', { isOpen, customerId: customer.id });
    }
  }, [isOpen, customer.id]);

  const loadCustomerInteractions = async () => {
    setIsLoading(true);
    try {
      console.log('🔍 Loading customer interactions for customer:', customer.id, customer.name);
      
      // ENHANCED: Multi-field customer search strategy with proper permissions
      // NOTE: Due to Directus permissions, we can only show current user's interactions with this customer
      console.log('🔍 Enhanced search: Using multiple customer identification fields...');
      console.log('📋 Customer data:', {
        id: customer.id,
        name: customer.name,
        idcust: customer.idcust,
        NamaPelangganTambak: customer.NamaPelangganTambak
      });

      // 🔧 IMPROVEMENT: Get completed interactions for this customer from all users
      // This shows only finished/completed interactions with limited details for other users
      const result = await directusOperations.getCustomerInteractions({
        limit: 50,
        filter: {
          _and: [
            // COMPREHENSIVE customer matching across all possible fields
            {
              _or: [
                // Standard No_pelanggan field (primary approach)
                { No_pelanggan: { _eq: customer.id.toString() } },
                { No_pelanggan: { _eq: customer.id } },
                
                // customer_tambak_id field (user's suggested enhancement)
                { customer_tambak_id: { _eq: customer.id } },
                { customer_tambak_id: { _eq: customer.id.toString() } },
                
                // Additional customer identification attempts
                ...(customer.idcust ? [
                  { No_pelanggan: { _eq: customer.idcust } },
                  { customer_tambak_id: { _eq: customer.idcust } }
                ] : []),
                
                // NOTE: Removed customer_name search - field doesn't exist in customer_Interaction table
                // Customer names would need to be retrieved via relationships:
                // - data_customer -> nama_pelanggan 
                // - customer_tambak -> nama_pelanggan_tambak
              ]
            },
            // Maintain visibility filtering
            {
              _or: [
                { Hidden: { _null: true } },
                { Hidden: { _eq: false } }
              ]
            },
            // FILTER: Only show completed interactions (done=true)
            { done: { _eq: true } }
          ]
        },
        sort: ['-date_created']
      });

      console.log('🎯 Enhanced search completed. Results found:', result.length);

      console.log('📊 Raw completed interactions from getCustomerInteractions (ALL users):', result.length);

      // If no results, show debugging info about the search criteria
      if (result.length === 0) {
        console.log('🔍 No completed interactions found for this customer from any user');
        console.log('💡 This may mean:');
        console.log('   • No users have completed interactions with this customer yet');
        console.log('   • Customer ID matching criteria need adjustment (No_pelanggan or customer_tambak_id)');
        console.log('   • All completed interactions for this customer might be hidden');
        console.log('📋 Search criteria used:', {
          userId: currentUserId,
          customerId: customer.id,
          customerIdcust: customer.idcust,
          customerName: customer.name
        });
      }

      // Use exact same normalization as working implementation
      const normalizeInteractionData = (item: any): Partial<CustomerInteractionRecord> => {
        return {
          id: item.id,
          No_pelanggan: item.No_pelanggan || 'UNKNOWN',
          date_created: item.date_created || new Date().toISOString(),
          priority: ['low', 'medium', 'high'].includes(item.priority) ? item.priority : 'low',
          keterangan: item.keterangan || '',
          interaction_type: item.interaction_type || 'WhatsApp',
          PIC: item.PIC || 'UNKNOWN',
          customer_tambak_id: item.customer_tambak_id || null,
          visit_id: item.visit_id || null,
          done: item.done,
          hidden: Boolean(item.Hidden),
          // NOTE: customer_name field doesn't exist in customer_Interaction table
          // Would need relationship lookups for customer names from data_customer/customer_tambak
          customer_name: customer.name || customer.NamaPelangganTambak || 'Unknown Customer',
          pic_name: item.pic_name || 'Unknown PIC',
          tambak_name: item.tambak_name || null
        };
      };

      // Apply same normalization as working implementation
      const normalizedInteractions = result.map((item: any) => normalizeInteractionData(item));
      
      // 🔧 FIX: Resolve user names from directus_users table
      console.log('🔍 Resolving PIC names from directus_users...');
      console.log('📋 PIC IDs to resolve:', normalizedInteractions.map(i => ({ id: i.id, PIC: i.PIC })));
      
      const resolvedInteractions = await Promise.all(
        normalizedInteractions.map(async (interaction, index) => {
          try {
            console.log(`🔍 [${index}] Resolving PIC "${interaction.PIC}" for interaction ${interaction.id}`);
            
            if (!interaction.PIC) {
              console.warn(`⚠️ [${index}] No PIC field for interaction ${interaction.id}`);
              return {
                ...interaction,
                pic_name: 'No PIC Assigned' 
              };
            }
            
            // Resolve PIC ID to actual user name from directus_users
            const picName = await resolveUserName(interaction.PIC);
            console.log(`✅ [${index}] Resolved PIC "${interaction.PIC}" → "${picName}"`);
            
            return {
              ...interaction,
              pic_name: picName // This replaces "Unknown PIC" with actual name
            };
          } catch (error) {
            console.error(`❌ [${index}] Failed to resolve user name for PIC:`, interaction.PIC, error);
            return {
              ...interaction,
              pic_name: interaction.PIC ? `User ${interaction.PIC}` : 'Unknown User' // Fallback with ID
            };
          }
        })
      );
      
      console.log('✅ PIC names resolved:', resolvedInteractions.length, 'interactions processed');
      
      // Transform to ActivityInteraction format using resolved interactions
      const activityInteractions: ActivityInteraction[] = resolvedInteractions.map((interaction) => ({
        ...interaction,
        status: determineInteractionStatus(interaction as CustomerInteractionRecord),
        isCurrentUser: interaction.PIC === currentUserId
      } as ActivityInteraction));

      console.log('✅ Final processed interactions:', activityInteractions.length, activityInteractions);
      setInteractions(activityInteractions);
    } catch (error) {
      console.error('❌ Failed to load customer interactions:', error);
      // Show empty state when no data can be fetched
      setInteractions([]);
    } finally {
      console.log('🏁 Finally block: Setting loading to false');
      setIsLoading(false);
    }
  };

  // Helper function to determine interaction status
  // NOTE: Since we now filter for done=true only, all interactions should be 'completed'
  const determineInteractionStatus = (interaction: CustomerInteractionRecord): 'pending' | 'completed' | 'overdue' => {
    if (interaction.done === true) {
      return 'completed';
    }
    
    if (interaction.done === false) {
      return 'pending'; // Cancelled interactions
    }
    
    // Check if overdue based on reminder date
    if (interaction.date_remind) {
      const reminderDate = new Date(interaction.date_remind);
      const now = new Date();
      if (reminderDate < now) {
        return 'overdue';
      }
    }
    
    return 'pending';
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'overdue':
        return <AlertTriangle className="w-4 h-4 text-red-600" />;
      case 'pending':
      default:
        return <Timer className="w-4 h-4 text-orange-600" />;
    }
  };

  const getInteractionTypeIcon = (type: string) => {
    switch (type) {
      case 'WhatsApp':
        return <MessageSquare className="w-4 h-4 text-green-600" />;
      case 'Phone Call':
        return <Phone className="w-4 h-4 text-primary" />;
      case 'Visit/Meeting':
        return <Users className="w-4 h-4 text-purple-600" />;
      default:
        return <MessageSquare className="w-4 h-4 text-muted-foreground" />;
    }
  };

  const formatDateTime = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return {
        date: date.toLocaleDateString('en-US', {
          day: '2-digit',
          month: 'short',
          year: 'numeric'
        }),
        time: date.toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit'
        }),
        relative: formatDistanceToNow(date, { 
          addSuffix: true, 
          locale: enUS 
        })
      };
    } catch {
      return {
        date: 'Invalid Date',
        time: '',
        relative: ''
      };
    }
  };

  if (!isOpen) return null;

  // Debug render state
  console.log('🖼️ CustomerActivityModal render state:', { 
    isOpen, 
    isLoading, 
    interactionsCount: interactions.length,
    customerId: customer.id,
    customerName: customer.name
  });

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center p-0 sm:p-4 sm:items-center">
      <div 
        className="fixed inset-0 bg-gray-900/50 transition-opacity"
        onClick={onClose}
      />
      
      <div className="relative bg-card rounded-t-xl sm:rounded-xl shadow-xl w-full max-w-md mx-auto max-h-[95vh] sm:max-h-[85vh] flex flex-col border border-border">
        {/* Header */}
        <div className="flex-shrink-0 p-4 border-b border-border">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-card-foreground">
                {t('customer_activity.title')}
              </h3>
              <p className="text-sm text-muted-foreground mt-1">
                {customer.name}
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-muted-foreground hover:text-card-foreground transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div 
          ref={scrollRef}
          className="flex-1 overflow-y-auto p-4 space-y-4"
        >
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : interactions.length === 0 ? (
            <div className="text-center py-8">
              <MessageSquare className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground text-sm">
                {t('customer_activity.no_interactions')}
              </p>
              <p className="text-muted-foreground text-xs mt-2">
                {t('customer_activity.showing_completed')}
              </p>
            </div>
          ) : (
            interactions.map((interaction) => {
              const dateTime = formatDateTime(interaction.date_created);
              
              return (
                <div 
                  key={interaction.id}
                  className="border border-border rounded-lg p-4 space-y-3 bg-card/50"
                >
                  {/* Header with User and Date */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <User className="w-4 h-4 text-muted-foreground" />
                      <span className="font-medium text-card-foreground">
                        {interaction.pic_name}
                      </span>
                      {interaction.isCurrentUser && (
                        <Badge variant="secondary" className="text-xs">
                          {t('customer_activity.you')}
                        </Badge>
                      )}
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-medium text-card-foreground">
                        {dateTime.date}
                      </div>
                    </div>
                  </div>

                  {/* Conditional Details Based on User Ownership */}
                  {interaction.isCurrentUser ? (
                    /* Current User - Show Full Details */
                    <div className="space-y-2 pl-6 border-l-2 border-primary/10">
                      {/* Type and Status Row */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          {getInteractionTypeIcon(interaction.interaction_type)}
                          <span className="text-sm text-card-foreground">
                            {interaction.interaction_type}
                          </span>
                        </div>
                        <div className="flex items-center space-x-2">
                          {getStatusIcon(interaction.status)}
                          <Badge variant={interaction.status === 'completed' ? 'default' : interaction.status === 'overdue' ? 'destructive' : 'secondary'} className="text-xs font-medium">
                            {interaction.status === 'completed' ? t('customer_activity.status_completed') :
                             interaction.status === 'overdue' ? t('customer_activity.status_overdue') : t('customer_activity.status_pending')}
                          </Badge>
                        </div>
                      </div>

                      {/* Description */}
                      {interaction.keterangan && (
                        <div className="text-sm text-card-foreground bg-muted/50 p-2 rounded">
                          {interaction.keterangan}
                        </div>
                      )}
                    </div>
                  ) : (
                    /* Other Users - Show Limited Details (Name + Date Only) */
                    <div className="pl-6 border-l-2 border-gray-200">
                      <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                        <HandshakeIcon className="w-3 h-3" />
                        <span>{t('customer_activity.interaction_completed')}</span>
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">
                        {t('customer_activity.has_interacted_with', { picName: interaction.pic_name, customerName: interaction?.customer_name })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};